import getClosest from "../get-closest/get-closest";
import { processCountrySelection, siteId } from "../utility-functions/utility-functions";
import { addClass, removeClass } from "@remarkablenet/rcm-utils/class-helpers";
import { getAntiForgeryToken } from "@remarkablenet/rcm-utils";
import { addDelegateEventListener } from "@remarkablenet/rcm-utils/add-delegate-event-listener";
import { slideoutPanel, openPanel, closePanel, isPanelOpen } from '../slideout-panel/slideout-panel';
import { fetchStoresSearch } from '../store-list/store-list';
import {
	panelUpdater,
	setPanelLoading,
	setPanelLoaded,
	type PanelUpdaterArgs,
	type PanelLoadingArgs
} from '@remarkablenet/rcm-utils'
import { scrollIntoView } from '../scroll-into-view/scroll-into-view';

let _completeCallback: Function;
let pendingDeliveryOptionId: string | null = null;

export function autoInitialise(completeCallback: Function) {
	console.log("Delivery accordion module loaded");
	slideoutPanel();

	const dynamicPartial: HTMLElement = document.querySelector("#dynamic-delivery");
	if (dynamicPartial === null) return;

	_completeCallback = completeCallback;
	addEventListeners(dynamicPartial);

	const country = siteId();
	if (country === "US" || country === "IE") {
		const options = document.querySelectorAll(".delivery-options[data-type='delivery'] .delivery-option.js-accordion-option");
		if (options.length === 1) {
			handleDeliveryOptionClick(options[0]);
		}
	}
}

function handleDeliveryOptionClick(targetElm) {
	removeHelpRequired();

	const deliveryOptionRow = getClosest(targetElm, [".js-accordion-option"]);

	const thisDeliveryOptionIsAlreadySelected = isDeliveryOptionRowSelected(deliveryOptionRow);
	let selectedValueMatchesLastValue = true;

	if (deliveryOptionHasSecondStep(deliveryOptionRow)) {
		const selectElm = deliveryOptionRow.querySelector("select");
		const selectedValue = selectElm.value;
		const lastVal = selectElm.dataset.lastValue;

		selectedValueMatchesLastValue = selectedValue === lastVal && selectedValue !== undefined;
		const selectedValueIsDefault = selectedValue === "-1" || selectedValue === "" || selectedValue === undefined;

		if (selectedValueIsDefault) {
			addHelpRequired(deliveryOptionRow);
			return;
		}

		if (selectedValueMatchesLastValue && thisDeliveryOptionIsAlreadySelected) return;
	}

	if (thisDeliveryOptionIsAlreadySelected && selectedValueMatchesLastValue && (deliveryOptionRow.dataset.type !== "click&collect")) return;

	if (deliveryOptionRow.dataset.type === "click&collect") {
		console.log('Open click & collect panel');

		const input = deliveryOptionRow.querySelector("input[type='radio']");
		if (input) {
			input.checked = false;
			pendingDeliveryOptionId = input.value;
		}

		openPanel('CC', fetchStoresSearch(null));
		return;
	}

	if (isPanelOpen('CC')) {
		closePanel('CC');
	}

	selectDeliveryOptionRow(deliveryOptionRow);
	sendDeliveryInformationToServer();
}

function isCountrySelect(select: HTMLSelectElement): boolean {
	return select.classList.contains("js-country-check") || select.dataset.type === "country";
}

function addEventListeners(context: HTMLElement | Document = document) {

	// .js-accordion-option click
	addDelegateEventListener({
		container: context,
		event: "click",
		target: ".js-accordion-option",
		callback: (e) => {
			handleDeliveryOptionClick(e.originalEvent.target);
		},
	});

	// custom-select li click
	addDelegateEventListener({
		container: context,
		event: "click",
		target: ".js-accordion-option .custom-select li",
		callback: (e) => {
			setDeliveryCountryCustomSelect(e.delegateTarget as HTMLLIElement);
		},
	});

	// select change (fallback/older browsers)
	addDelegateEventListener({
		container: context,
		event: "change",
		target: ".js-accordion-option select",
		callback: (e) => {
			const select = e.delegateTarget as HTMLSelectElement;

			if (isCountrySelect(select)) {
				processCountrySelection(select);
				return;
			}

			handleDeliveryOptionClick(e.originalEvent.target);
		},
	});

	const deliverySelectionOptions = document.querySelectorAll(".js-delivery-selection") as NodeListOf<HTMLElement>;

	if (deliverySelectionOptions.length > 0) {

		addDelegateEventListener({
			container: context,
			event: "click",
			target: ".js-delivery-selection",
			callback: (e) => {
				const option = e.delegateTarget as HTMLElement;
				const type = option.dataset.type;

				context.querySelectorAll(".js-delivery-selection").forEach((option) => {
					option.classList.remove("active");
				});

				option.classList.add('active');

				setActiveDeliveryType(type);
			},
		});

	}
}

function setActiveDeliveryType(type) {
	if (type === 'delivery' && isPanelOpen('CC')) {
		closePanel('CC');
	}

	const containers = document.querySelectorAll(`.delivery-options`) as NodeListOf<HTMLElement>;
	const container = document.querySelector(`.delivery-options[data-type='${type}']`);

	containers.forEach((item) => {
		item.classList.add("d-none");
	});

	container.classList.remove("d-none");
}

function setDeliveryCountryCustomSelect(selectedOption: HTMLLIElement) {
	const countrySelector = selectedOption.closest(".custom-select").querySelector("select");
	countrySelector.value = selectedOption.getAttribute("data-value");
	handleDeliveryOptionClick(countrySelector);
}

function selectDeliveryOptionRow(deliveryOptionRow) {
	const checkbox = deliveryOptionRow.querySelector('[type="radio"]');

	removeSelectedOptionStates();
	checkbox.checked = true;
	addClass({ elm: deliveryOptionRow, className: ["is-selected"] });
}

function getParentAccordionPanel(elm) {
	return getClosest(elm, ".js-accordion-panel");
}

function getSelectedOptionElm(context = document) {
	return context.querySelector(".js-accordion-option.is-selected");
}

function isPanelInChoosingState(panel) {
	return panel.classList.contains("is-choosing");
}
function isDeliveryOptionRowSelected(elm) {
	return elm.classList.contains("is-selected");
}

function deliveryOptionHasSecondStep(deliveryOptionRow) {
	const hasSecondStepByNestedSelect = deliveryOptionRow.querySelector("select") !== null;
	return hasSecondStepByNestedSelect;
}

function removeSelectedOptionStates() {
	document.querySelectorAll(".js-accordion-option").forEach((elm) => elm.classList.remove("is-selected"));
}

function removeHelpRequired() {
	(document.querySelectorAll(".is-invalid") as NodeListOf<HTMLElement>).forEach((elm) =>
		removeClass({ elm, className: ["is-invalid"] })
	);
}

function addHelpRequired(deliveryOptionsRow) {
	addClass({ elm: deliveryOptionsRow, className: ["is-invalid"] });
}

function getDeliveryData() {
	const deliveryContainer = document.querySelector("#delivery-options-container");
	const selectedDeliveryOption = getSelectedOptionElm();
	const countryID = (document.getElementById("ddlCountry") as HTMLInputElement).value;
	const storeId = (document.getElementById("ddlStoreId") as HTMLInputElement).value;
	const checkedDelivery = deliveryContainer.querySelector('input[name="deliveryOption"]:checked') as HTMLInputElement;

	let selectedDeliveryOptionID = checkedDelivery ? parseInt(checkedDelivery.value) : 0;
	if (pendingDeliveryOptionId) {
		selectedDeliveryOptionID = parseInt(pendingDeliveryOptionId);
		pendingDeliveryOptionId = null;
	}

	let data = {
		DeliveryOptionID: selectedDeliveryOptionID,
		DeliveryCountryID: countryID,
	};
	if (!selectedDeliveryOption) return;

	if (storeId !== "-1") {
		data["StoreID"] = storeId;
	}

	const selectElm = selectedDeliveryOption.querySelector("select");
	if (selectElm) {
		if (selectElm.dataset.type === "statedDay") {
			const option = selectElm.value;
			const optionSplit = option.split(" ");

			data["StatedDay"] = optionSplit[0];
		}

		data.DeliveryCountryID = selectElm.value;
	}

	return data;
}

export const sendDeliveryInformationToServer = (extraData = {}) => {
	const dynamicPartial = document.getElementById("dynamic-delivery");
	const url = "/Checkout/Delivery/UpdateSelectedDeliveryOption";
	const disabledPanel = document.querySelector('.checkout-button-container');

	let data = getDeliveryData();

	console.log(data);

	const sendtoserverpromise = fetch(url, {
		headers: {
			requestverificationtoken: getAntiForgeryToken(),
			"content-type": "application/json",
		},
		method: "post",
		credentials: "include",
		body: JSON.stringify({ ...data, ...extraData }),
	})
		.then((resp) => resp.text())
		.then((responseText) => {
			dynamicPartial.innerHTML = responseText;
		});

	setTimeout(function () {
		disabledPanel.classList.remove('disabled');
		scrollIntoView(disabledPanel, { scrollUp: 300, scrollDown: 300 });
	}, 1200);

	return panelUpdater(dynamicPartial, sendtoserverpromise);
};

export const refreshPanel = (_completeCallback, data) => {
	sendDeliveryInformationToServer(data).then(_completeCallback);
};

export function selectDeliveryOptionByRowSelector(selector) {
	const deliveryOptionRow = document.querySelector(selector);

	if (deliveryOptionRow === null) return;

	handleDeliveryOptionClick(deliveryOptionRow);
}

export function setInternalJsonData(data) {
	const jsonContainer: HTMLInputElement = document.querySelector("#integrationJsonData");
	if (jsonContainer === null) return;

	jsonContainer.value = JSON.stringify(data);
}
